import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  CASH_REQUEST_ENTITY_CODE,
  CashRequest,
  CashRequestCandidates,
  CashRequestDocumentsUpdate,
  CashRequestHeaderUpdate,
  CashRequestInsert,
  CashRequestLineInsert,
  CashRequestManualEntriesUpdate,
  CashRequestStatus,
  CashRequestUsage,
  FinancialLineType,
} from '../models/cash-request/cash-request';

/** Server-side narrowing for the candidate query. The picker filters what it already holds and only
 *  re-queries for `includePlanned`, so these exist for callers that need a narrower page. */
export interface CashRequestCandidateQuery {
  /** Include candidates already planned into ANOTHER cash request (excluded by default). */
  includePlanned?: boolean;
  search?: string | null;
  providerReference?: string | null;
  glAccountReference?: string | null;
}

@Injectable({ providedIn: 'root' })
export class CashRequestProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/cash-requests';
  private readonly lineTypeUrl = environment.proxyUrls.financeApi + '/api/v1/financial-line-types';

  public search(
    companyReference: string,
    status?: CashRequestStatus | null,
    weekStart?: string | null,
    weekEnd?: string | null,
  ): Observable<ApiResponse<CashRequest[]>> {
    let params = new HttpParams().set('companyReference', companyReference);
    if (status != null) params = params.set('status', status);
    if (weekStart) params = params.set('weekStart', weekStart);
    if (weekEnd) params = params.set('weekEnd', weekEnd);
    return this.httpClient.get<ApiResponse<CashRequest[]>>(this.baseUrl, { params });
  }

  public getById(id: string): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.get<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}`);
  }

  /**
   * The request as a flat workbook, one row per breakdown entry (#5944). Returns the file itself rather than
   * an ApiResponse envelope, matching the other workbook endpoints.
   */
  public export(id: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}/${id}/export`, { responseType: 'blob' });
  }

  /**
   * How much of the request has actually been spent, and on what (#4796).
   *
   * A read-side aggregate over the AP payment lines tagged to this request — separate from `getById` because
   * it summarises payments, not the request, and only the details page needs it.
   */
  public getUsage(id: string): Observable<ApiResponse<CashRequestUsage>> {
    return this.httpClient.get<ApiResponse<CashRequestUsage>>(`${this.baseUrl}/${id}/usage`);
  }

  public create(insert: CashRequestInsert): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.post<ApiResponse<CashRequest>>(this.baseUrl, insert);
  }

  /** Edit a Draft request's header (week, currency, notes/description). */
  public updateHeader(id: string, update: CashRequestHeaderUpdate): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.put<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}`, update);
  }

  /**
   * Everything the document picker can offer — payment requests AND withholdings — in ONE call.
   * Candidates already on THIS request come back flagged rather than hidden, so the picker can show them
   * checked and let the user unplan from the same place they planned.
   */
  public getCandidates(id: string, query: CashRequestCandidateQuery = {}): Observable<ApiResponse<CashRequestCandidates>> {
    let params = new HttpParams().set('includePlanned', query.includePlanned ?? false);
    if (query.search) params = params.set('search', query.search);
    if (query.providerReference) params = params.set('providerReference', query.providerReference);
    if (query.glAccountReference) params = params.set('glAccountReference', query.glAccountReference);
    return this.httpClient.get<ApiResponse<CashRequestCandidates>>(`${this.baseUrl}/${id}/candidates`, { params });
  }

  /**
   * Plan/unplan source documents. A DELTA, not the full selection — the picker filters, so sending
   * "everything selected" would unplan whatever the active filter hid.
   *
   * The caller names no line: a source is routed to a line by its own AP control account, and one spanning
   * several accounts lands as one entry per account.
   */
  public updateDocuments(id: string, update: CashRequestDocumentsUpdate): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.post<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/documents`, update);
  }

  /** Save ONE line's manual entries. Document entries on that line are left untouched. */
  public updateManualEntries(id: string, lineId: string, update: CashRequestManualEntriesUpdate): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.put<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/lines/${lineId}/manual-entries`, update);
  }

  public addLine(id: string, line: CashRequestLineInsert): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.post<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/lines`, line);
  }

  public updateLine(id: string, lineId: string, line: CashRequestLineInsert): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.put<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/lines/${lineId}`, line);
  }

  public removeLine(id: string, lineId: string): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.delete<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/lines/${lineId}`);
  }

  /** Attach a supporting document to an already-saved line (multipart). */
  public uploadLineAttachment(id: string, lineId: string, file: File): Observable<ApiResponse<CashRequest>> {
    const form = new FormData();
    form.append('file', file);
    return this.httpClient.post<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/lines/${lineId}/attachment`, form);
  }

  public submit(id: string): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.post<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/submit`, {});
  }

  public approve(id: string): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.post<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/approve`, {});
  }

  public cancel(id: string): Observable<ApiResponse<CashRequest>> {
    return this.httpClient.post<ApiResponse<CashRequest>>(`${this.baseUrl}/${id}/cancel`, {});
  }

  /** The CashRequest line-type catalog (planillas, anticipos, impuestos, facturas, …). */
  public getLineTypes(): Observable<ApiResponse<FinancialLineType[]>> {
    const params = new HttpParams().set('entityDefinitionCode', CASH_REQUEST_ENTITY_CODE).set('includeInactive', false);
    return this.httpClient.get<ApiResponse<FinancialLineType[]>>(this.lineTypeUrl, { params });
  }

  /**
   * Deletes a DRAFT document (#5223). The API refuses anything further along, so this is safe to offer
   * from a draft screen and safe to have refused if the status moved on in another tab.
   */
  public delete(id: string): Observable<ApiResponse<string>> {
    return this.httpClient.delete<ApiResponse<string>>(`${this.baseUrl}/${id}`);
  }
}
